<template>
  <div class="webview-root page">
    <!-- 加载态 -->
    <div v-if="loading" class="status-tip">加载中...</div>

    <!-- 错误态 -->
    <template v-else-if="error">
      <div class="status-tip error">{{ error }}</div>
    </template>

    <!-- 内容区 -->
    <template v-else-if="detail">
      <!-- 标题区 -->
      <div class="page-title">
        <h1 class="title-main">{{ detail.typeName || '' }}</h1>
        <p v-if="detail.groupName" class="title-sub">{{ detail.groupName }}</p>
      </div>

      <!-- 属性表 -->
      <div class="section" v-if="detail.attrs && detail.attrs.length">
        <div class="section-header">
          <span class="section-title">赛事说明</span>
          <span v-if="detail.annex && detail.annex.length" class="section-action" @click="handleAnnex">附件 ›</span>
        </div>
        <div class="attrs-table">
          <div class="attrs-row" v-for="(attr, i) in detail.attrs" :key="i">
            <span class="attr-key">{{ attr.key }}</span>
            <span class="attr-value">{{ attr.value }}</span>
          </div>
        </div>
      </div>

      <!-- 富文本区 -->
      <div class="section" v-if="detail.rule">
        <div class="rich-text-root" v-html="safeHtml"></div>
      </div>
    </template>

    <!-- 空数据态 -->
    <div v-else class="status-tip">暂无数据</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getEventDetail } from '@/services/apis'
import { sanitizeHtml } from '@/utils/sanitize'
import '@/styles/richtext.css'

/** 活动详情数据结构 */
interface EventDetail {
  typeName?: string
  groupName?: string
  attrs?: { key: string; value: string }[]
  rule?: string
  annex?: { name?: string; url?: string }[]
}

const route = useRoute()
const detail = ref<EventDetail | null>(null)
const loading = ref(true)
const error = ref('')

/**
 * 获取活动 ID
 * 小程序 web-view 通过 URL query 传入，例如 /richtext?id=2239
 */
function getEventId(): string {
  return (route.query.id as string) || '2239'
}

/** 从 URL query 中读取分组名称，优先级高于接口返回的 groupName */
function getGroupName(): string {
  const q = route.query.groupName as string
  return q ? decodeURIComponent(q) : ''
}

/** 加载活动详情 */
async function loadDetail() {
  loading.value = true
  error.value = ''
  try {
    const id = getEventId()
    // apis.js 为无类型 JS 文件，此处断言为 EventDetail 以获得类型提示
    const data = await getEventDetail(id) as unknown as EventDetail
    if (data) {
      // URL 传入的 groupName 优先级高于接口返回，方便上级页面控制显示
      const groupName = getGroupName()
      if (groupName) data.groupName = groupName
      detail.value = data
    } else {
      error.value = '数据为空'
    }
  } catch (e: any) {
    error.value = `加载失败：${e?.message || String(e)}`
  } finally {
    loading.value = false
  }
}

/**
 * 加载微信 JS-SDK（jweixin），供 web-view 调用小程序能力
 */
function loadWechatSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any)?.wx?.miniProgram) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('微信 JS-SDK 加载失败'))
    document.head.appendChild(script)
  })
}

/**
 * 附件点击：通过 postMessage 将附件数据传给小程序宿主页面，再跳转到小程序附件页面
 *
 * 数据流：
 *   H5 wx.miniProgram.postMessage → 小程序宿主页面 bindmessage → wx.setStorageSync → pages/event-files 读取
 *
 * ⚠️ 注意：此函数依赖 postMessage 传数据，小程序侧需在 web-view 宿主页面添加 bindmessage 监听，
 *    收到 { type: 'attachments', payload: [...] } 后写入 storage（key: event-attachments），
 *    再跳转 pages/event-files。详见下方注释。
 */
async function handleAnnex() {
  const files = detail.value?.annex
  if (!files?.length) return
  try {
    await loadWechatSDK()
    const wx = (window as any).wx
    // 通过 postMessage 将附件数据传递给小程序宿主页面（无大小限制，不经过 URL）
    wx.miniProgram.postMessage({ data: { type: 'attachments', payload: files } })
    // 跳转到小程序附件页面，数据由宿主页面中转写入 storage
    wx.miniProgram.navigateTo({ url: '/pages/event-files' })
  } catch (e) {
    console.warn('跳转小程序页面失败（非微信环境？）', e)
  }
}
/**
 * ──────────── 小程序侧需配合的改动 ────────────
 *
 * 1. web-view 宿主页面（WXML）：
 *    <web-view src="..." bindmessage="onH5Message" />
 *
 * 2. web-view 宿主页面（JS）：
 *    onH5Message(e) {
 *      const { type, payload } = e.detail.data[0] || {}
 *      if (type === 'attachments') {
 *        wx.setStorageSync('event-attachments', payload)
 *      }
 *    }
 *
 * 3. pages/event-files 页面 onLoad：
 *    onLoad() {
 *      // postMessage 触发时机晚于 navigateTo，加短延时确保数据已写入
 *      setTimeout(() => {
 *        const files = wx.getStorageSync('event-attachments') || []
 *        this.setData({ files })
 *      }, 300)
 *    }
 * ──────────────────────────────────────────
 */

/**
 * 修复富文本中的表格，兼容微信小程序 web-view 渲染
 * - 移除 colgroup/col（小程序不支持）
 * - 强制 table width:100% + border-collapse
 * - 为 td/th 注入边框和内边距，移除冲突属性
 */
function fixTableForMiniProgram(html: string): string {
  if (!html) return ''
  let res = html
  // 移除 colgroup / col
  res = res.replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '')
  res = res.replace(/<col[^>]*\/?>/gi, '')
  // 修复 table：清除冲突属性，注入固定样式
  res = res.replace(/<table([^>]*)>/gi, (_, attrs: string) => {
    const cleaned = attrs
      .replace(/\s+(?:width|border|cellpadding|cellspacing)=(?:"[^"]*"|'[^']*'|\S+)/gi, '')
      .replace(/\s+style=(?:"[^"]*"|'[^']*')/gi, '')
    return `<table${cleaned} style="width:100%;border-collapse:collapse;">`
  })
  // 修复 td/th：清除冲突属性，注入边框和内边距
  const fixCell = (tag: string) => {
    const re = new RegExp(`<${tag}([^>]*)>`, 'gi')
    res = res.replace(re, (_, attrs: string) => {
      const cleaned = attrs
        .replace(/\s+width=(?:"[^"]*"|'[^']*'|\S+)/gi, '')
        .replace(/\s+style=(?:"[^"]*"|'[^']*')/gi, '')
      return `<${tag}${cleaned} style="border:1px solid #999;padding:6px;word-break:break-all;vertical-align:middle;">`
    })
  }
  fixCell('td')
  fixCell('th')
  return res
}

/** 安全且修复后的富文本 HTML（DOMPurify 清洗 → 小程序表格修复） */
const safeHtml = computed(() => {
  const raw = detail.value?.rule
  if (!raw) return ''
  const clean = sanitizeHtml(raw, {
    ALLOWED_TAGS: [
      'b','i','em','strong','a','p','ul','ol','li','table','tr','td','th','thead','tbody',
      'img','div','span','br','h1','h2','h3','h4','h5','h6','hr','blockquote','pre','code'
    ],
    ALLOWED_ATTR: ['href','src','alt','title','width','height','style','colspan','rowspan','target']
  })
  return fixTableForMiniProgram(clean)
})

onMounted(() => { loadDetail() })
</script>

<style scoped>
.page { padding: 16px; min-height: 100vh; box-sizing: border-box; background: #f5f5f5; }

/* 通用状态提示 */
.status-tip { text-align: center; padding: 60px 0; color: #999; font-size: 14px; }
.status-tip.error { color: #e74c3c; }

/* 标题区 */
.page-title { text-align: center; padding: 24px 16px 16px; background: #fff; border-radius: 8px; margin-bottom: 12px; }
.title-main { font-size: 18px; font-weight: 600; color: #333; margin: 0 0 8px; line-height: 1.4; }
.title-sub { font-size: 13px; color: #999; margin: 0; line-height: 1.4; }

/* 通用区块 */
.section { background: #fff; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #eee; }
.section-title { font-size: 15px; font-weight: 600; color: #333; }
.section-action { font-size: 13px; color: #1677ff; cursor: pointer; }

/* 属性表 */
.attrs-table { display: flex; flex-direction: column; }
.attrs-row { display: flex; border-bottom: 1px solid #f0f0f0; }
.attrs-row:last-child { border-bottom: none; }
.attr-key { width: 35%; flex-shrink: 0; padding: 10px 8px; font-size: 13px; color: #666; background: #fafafa; display: flex; align-items: center; }
.attr-value { flex: 1; padding: 10px 8px; font-size: 13px; color: #333; display: flex; align-items: center; word-break: break-all; }
.attr-key + .attr-value { border-left: 1px solid #f0f0f0; }

/* 富文本区 */
.rich-text-root { font-size: 14px; line-height: 1.8; color: #333; overflow-x: auto; }
</style>
